/**
 * Asking for a permission you do not hold, without React (WALLIYO-0131).
 *
 * What may be asked for, and what a well-formed ask looks like. The answer is
 * somebody else's; this only decides whether the question is worth sending.
 */

import type { GetStaffMeResponse } from "@wallyo/contracts/console";
import { PERMISSIONS, type DefinedPermissionId } from "@wallyo/contracts/permissions";

import { t } from "../../i18n";
import { REASON_MIN_LENGTH } from "../team/team-writes";

/** A permission somebody could ask for: what it is, and what it stands on. */
export type Askable = {
  settingId: DefinedPermissionId;
  feature: string;
};

/**
 * What this person could ask for.
 *
 * Only `approvable` permissions: the catalogue's own word for one where
 * somebody without it may ask rather than simply be refused. A permission
 * that is not approvable is a deliberate wall, and offering a form against it
 * would be an invitation to a refusal nobody can grant.
 *
 * What they already hold is left out — there is nothing to ask for — and so
 * is anything retired.
 */
export function askableBy(me: GetStaffMeResponse): readonly Askable[] {
  const held = new Set(me.permissions.map((permission) => permission.settingId));
  return PERMISSIONS.filter(
    (permission) =>
      permission.status !== "retired" &&
      permission.approvable &&
      !held.has(permission.settingId as DefinedPermissionId),
  ).map((permission) => ({
    settingId: permission.settingId as DefinedPermissionId,
    feature: permission.feature,
  }));
}

/** The ask, as the endpoint takes it. An open-ended one sends no date. */
export function askRequest(settingId: string, until: string, reason: string) {
  return {
    settingId,
    until: until ? new Date(`${until}T23:59:59.999Z`).toISOString() : null,
    reason: reason.trim(),
  };
}

/**
 * What stops an ask being sent.
 *
 * A date already past is the only one about the ask itself; the rest is the
 * same as any other write. Nothing here judges whether they should have it —
 * that is the question, and answering it here would defeat the point.
 */
export function askProblem(settingId: string, until: string, reason: string, now: Date = new Date()): string | null {
  if (settingId === "") return t("access.ask.pickOne");
  if (until !== "" && new Date(`${until}T23:59:59.999Z`).getTime() <= now.getTime()) {
    return t("access.ask.pastDate");
  }
  if (reason.trim().length < REASON_MIN_LENGTH) return t("features.save.reasonNeeded");
  return null;
}
